/**
 * Shared types, constants, and validation helpers for the CoreFlow Analytics module.
 */

// ─── Enums / Union Types ────────────────────────────────────────────────────

/** Supported chart visualization types */
export type ChartType =
  | 'bar'
  | 'horizontal-bar'
  | 'line'
  | 'pie'
  | 'stacked-bar'
  | 'table'
  | 'burndown'
  | 'velocity'
  | 'cumulative-flow'
  | 'histogram'
  | 'wip-aging'
  | 'sprint-report';

/** Groupable attribute of card data used as an axis or category */
export type Dimension =
  | 'list'
  | 'swimlane'
  | 'label'
  | 'assignee'
  | 'custom-field'
  | 'due-date-week'
  | 'due-date-month'
  | 'board'
  | 'wip-status'
  | 'sprint-field'
  | 'linked-to-list';

/** Numeric aggregation applied to card data */
export type Measure =
  | { type: 'card-count' }
  | { type: 'numeric-field-sum'; fieldId: string }
  | { type: 'numeric-field-avg'; fieldId: string }
  | { type: 'cycle-time-avg' }
  | { type: 'lead-time-avg' }
  | { type: 'throughput'; bucketSize: 'daily' | 'weekly' | 'biweekly' }
  | { type: 'card-age' };

// ─── Interfaces ─────────────────────────────────────────────────────────────

/** Analytics dashboard metadata */
export interface AnalyticsDashboard {
  readonly dashboardId: string;
  readonly title: string;
  readonly visibility: 'private' | 'public-read';
  readonly widgetLayout: WidgetLayoutEntry[];
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly version: number;
}

/** Position and size of a widget within the dashboard grid */
export interface WidgetLayoutEntry {
  readonly widgetId: string;
  readonly col: number;
  readonly row: number;
  readonly width: number;
  readonly height: number;
}

/** Analytics widget configuration */
export interface AnalyticsWidget {
  readonly widgetId: string;
  readonly dashboardId: string;
  readonly title: string;
  readonly chartType: ChartType;
  readonly dataSourceBoardIds: string[];
  readonly dimension: Dimension;
  readonly measure: Measure;
  readonly filters: QueryFilter[];
  readonly createdAt: string;
  readonly version: number;
  readonly burndownConfig?: BurndownConfig;
  readonly velocityConfig?: VelocityConfig;
  readonly sprintReportConfig?: SprintReportConfig;
}

/** Filter condition for analytics queries */
export interface QueryFilter {
  readonly field: 'label' | 'assignee' | 'date-range' | 'list' | 'swimlane' | 'custom-field' | 'linked-to-list';
  readonly operator: 'in' | 'between' | 'equals';
  readonly value: string | string[] | { start: string; end: string };
}

/** Payload for an analytics aggregation query */
export interface QueryPayload {
  readonly boardIds: string[];
  readonly dimension: Dimension;
  readonly measure: Measure;
  readonly filters?: QueryFilter[];
}

/** Single row of aggregated query results */
export interface QueryResultRow {
  readonly dimensionValue: string;
  readonly measureValue: number;
}

/** Payload for a drill-down query into individual cards */
export interface DrillDownPayload {
  readonly boardIds: string[];
  readonly dimension: Dimension;
  readonly dimensionValue: string;
  readonly measure: Measure;
  readonly filters?: QueryFilter[];
}

/** Card summary returned by drill-down queries */
export interface DrillDownCard {
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly boardId: string;
  readonly boardName: string;
  readonly listName: string;
  readonly assignee: string | null;
}

/** Payload for card change events consumed by the sync pipeline */
export interface CardEventPayload {
  readonly boardId: string;
  readonly cardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly listId: string;
  readonly listTitle: string;
  readonly swimlaneId: string;
  readonly swimlaneTitle: string;
  readonly labels: string[];
  readonly assigneeUserIds: string[];
  readonly dueDate: string | null;
  readonly startDate: string | null;
  readonly createdAt: string;
  readonly customFields: Record<string, unknown>;
  readonly archived: boolean;
  readonly previousListId: string | null;
  readonly previousListTitle: string | null;
}

/** Denormalized card snapshot stored in the Analytics Store (S3 Tables / Iceberg) */
export interface CardSnapshot {
  readonly cardId: string;
  readonly boardId: string;
  readonly cardNumber: number;
  readonly title: string;
  readonly listId: string;
  readonly listTitle: string;
  readonly swimlaneId: string;
  readonly swimlaneTitle: string;
  readonly labels: string[];
  readonly assigneeUserIds: string[];
  readonly dueDate: string | null;
  readonly startDate: string | null;
  readonly createdAt: string;
  readonly customFields: Record<string, unknown>;
  readonly archived: boolean;
  readonly syncedAt: string;
}

/** Event types that trigger card snapshots */
export type CardEventType = 'card.created' | 'card.updated' | 'card.moved' | 'card.archived';

/** Historical snapshot with additional metadata for time-series queries */
export interface HistoricalSnapshot extends CardSnapshot {
  readonly snapshotType: 'history';
  readonly eventType: CardEventType;
  readonly isDoneList: boolean;
  readonly previousListId: string | null;
  readonly previousListTitle: string | null;
  readonly correlationId: string;
}

// ─── PM Analytics Config Interfaces ─────────────────────────────────────────

/** Configuration for a burndown chart widget */
export interface BurndownConfig {
  readonly startDate: string;
  readonly endDate: string;
  readonly scopeFilter: QueryFilter;
  readonly pointsFieldId?: string;
}

/** Single data point in a burndown chart */
export interface BurndownDataPoint {
  readonly date: string;
  readonly remaining: number;
  readonly ideal: number;
  readonly totalScope: number;
}

/** Date range configuration for a sprint value */
export interface DateRangeConfig {
  readonly sprintLabel: string;
  readonly startDate: string;
  readonly endDate: string;
}

/** Configuration for a velocity chart widget */
export interface VelocityConfig {
  readonly groupingMode: 'sprint-field' | 'weekly' | 'biweekly';
  readonly pointsFieldId?: string;
  readonly dateRanges?: DateRangeConfig[];
  readonly sprintFieldName?: string;
}

/** Configuration for a sprint report widget */
export interface SprintReportConfig {
  /** Custom field ID for story points (optional — omit for count-only metrics) */
  readonly pointsFieldId?: string;
}

/** Single period in a velocity chart */
export interface VelocityDataPoint {
  readonly label: string;
  readonly count: number;
  readonly points?: number;
  readonly trailingAvg?: number;
}

/** Result of a cycle time calculation for a single card */
export interface CycleTimeResult {
  readonly cardId: string;
  readonly cardTitle: string;
  readonly cycleTimeDays: number;
  readonly startedAt: string;
  readonly completedAt: string;
}

/** Result of a lead time calculation for a single card */
export interface LeadTimeResult {
  readonly cardId: string;
  readonly cardTitle: string;
  readonly leadTimeDays: number;
  readonly createdAt: string;
  readonly completedAt: string;
}

/** WIP status categories */
export type WipStatus = 'under' | 'at' | 'over' | 'no-limit';

/** WIP data for a single list */
export interface WipListData {
  readonly listId: string;
  readonly listTitle: string;
  readonly cardCount: number;
  readonly wipLimit: number | null;
  readonly status: WipStatus;
  readonly cards: WipCardAge[];
}

/** Card age data for WIP analysis */
export interface WipCardAge {
  readonly cardId: string;
  readonly cardTitle: string;
  readonly ageDays: number;
}

/** Single data point for throughput chart */
export interface ThroughputDataPoint {
  readonly bucketLabel: string;
  readonly count: number;
  readonly points?: number;
  readonly movingAvg?: number;
}

/** Single data point for cumulative flow diagram */
export interface CumulativeFlowDataPoint {
  readonly date: string;
  readonly listTitle: string;
  readonly cardCount: number;
}

// ─── Validation Constants ───────────────────────────────────────────────────

/** Minimum length of a dashboard or widget title (after trimming) */
export const TITLE_MIN_LENGTH = 1;

/** Maximum length of a dashboard or widget title (after trimming) */
export const TITLE_MAX_LENGTH = 100;

/** Minimum number of data source boards per widget/query */
export const BOARD_COUNT_MIN = 1;

/** Maximum number of data source boards per widget/query */
export const BOARD_COUNT_MAX = 10;

/** All valid dimension values */
export const VALID_DIMENSIONS: readonly Dimension[] = [
  'list',
  'swimlane',
  'label',
  'assignee',
  'custom-field',
  'due-date-week',
  'due-date-month',
  'board',
  'wip-status',
  'sprint-field',
  'linked-to-list',
] as const;

/** All valid measure types */
export const VALID_MEASURE_TYPES = [
  'card-count',
  'numeric-field-sum',
  'numeric-field-avg',
  'cycle-time-avg',
  'lead-time-avg',
  'throughput',
  'card-age',
] as const;

// ─── Validation Helpers ─────────────────────────────────────────────────────

/** Result of an analytics validation check */
export interface AnalyticsValidationResult {
  readonly valid: boolean;
  readonly error?: string;
}

/**
 * Validates a dashboard or widget title.
 * Title must be 1–100 characters after trimming leading/trailing whitespace.
 */
export function validateTitle(title: string): AnalyticsValidationResult {
  const trimmed = title.trim();
  if (trimmed.length < TITLE_MIN_LENGTH) {
    return { valid: false, error: 'Title must not be empty after trimming whitespace' };
  }
  if (trimmed.length > TITLE_MAX_LENGTH) {
    return { valid: false, error: `Title must not exceed ${TITLE_MAX_LENGTH} characters` };
  }
  return { valid: true };
}

/**
 * Validates that the board count is within the allowed range (1–10).
 */
export function validateBoardCount(count: number): AnalyticsValidationResult {
  if (count < BOARD_COUNT_MIN) {
    return { valid: false, error: 'At least one board must be selected' };
  }
  if (count > BOARD_COUNT_MAX) {
    return { valid: false, error: `Cannot select more than ${BOARD_COUNT_MAX} boards` };
  }
  return { valid: true };
}

/**
 * Validates a query payload has all required fields:
 * - at least one boardId
 * - exactly one dimension (valid enum value)
 * - exactly one measure (valid type)
 */
export function validateQueryPayload(payload: Partial<QueryPayload>): AnalyticsValidationResult {
  if (!payload.boardIds || payload.boardIds.length === 0) {
    return { valid: false, error: 'At least one boardId is required' };
  }

  if (!payload.dimension) {
    return { valid: false, error: 'A dimension is required' };
  }
  if (!(VALID_DIMENSIONS as readonly string[]).includes(payload.dimension)) {
    return { valid: false, error: `Invalid dimension: ${payload.dimension}` };
  }

  if (!payload.measure) {
    return { valid: false, error: 'A measure is required' };
  }
  if (!(VALID_MEASURE_TYPES as readonly string[]).includes(payload.measure.type)) {
    return { valid: false, error: `Invalid measure type: ${payload.measure.type}` };
  }

  return { valid: true };
}
